/**
 * A timer object with getPromise and cancel methods
 * @typedef {Object} CancelableTimer
 * @property getPromise - Returns a promise, that will be resolved with the result of the deferred function of this
 *                        timer
 * @property cancel     - Cancel this timer
 */

/**
 * Function, that returns a CancelableTimer
 * Parameters provided to this function will be passed through to deferred function.
 * @name CancelableTimerFactory
 * @function
 * @return {CancelableTimer}
 */

/**
 * Returns a factory function, that can be used to create cancelable timers.
 *
 * @param func                       - A function to be executed after the specified period
 * @param millis                     - The period, after that the specified function will be executed
 * @return {CancelableTimerFactory}  - A factory function to create and start the cancelable timer
 */
export function makeTimer(func, millis) {
    let timeoutId
    return function startTimer(...args) {
        let rejectFn
        const promise = new Promise((resolve, reject) => {
            rejectFn = reject
            timeoutId = setTimeout(() => resolve(func.apply(null, args)), millis)
        })
        return {
            getPromise: () => promise,
            cancel: () => {
                clearTimeout(timeoutId)
                const error = new Error('Timer has been canceled')
                error.name = 'ECANCELED'
                rejectFn(error)
            }
        }
    }
}

/**
 * Function, that returns a promise from a CancelableTimer
 * Parameters provided to this function will be passed through to debounced function.
 * @name DebouncerFactory
 * @function
 * @return {Promise}
 */

/**
 * Returns a factory function, that wraps the specified function with a debouncer (code that can only be executed once
 * within a certain period). Subsequent calls to the debounced function within the debouncer period defer the actual
 * execution again by the specified period.
 *
 * @param func                - A function to be executed once within the specified period
 * @param millis              - The period, the debouncer will wait until actual execution
 * @return {DebouncerFactory} - A factory function to create and start a debounced operation
 */
export function makeDebounce(func, millis) {
    const startTimer = makeTimer(func, millis)
    let timer
    return function startDebounce(...args) {
        if (timer !== undefined) {
            timer.cancel()
        }
        timer = startTimer(...args)
        return timer.getPromise().catch(() => {})
    }
}
